import {observe} from '@github/selector-observer'
import approved_handles from '../../../config/site/approved_handles.json'

const ppi = window.devicePixelRatio || 1
const avatarSize = 54 * ppi
const avatarStrokeWidth = 9 * ppi
const avatarCount = 11
const iconStrokeWidth = 8 * ppi
const iconSize = 30 * ppi
const iconInset = 7 * ppi
const vectorPointCount = 18
const vectorPointSize = 12 * ppi
const isProd = !location.hostname.includes('localhost')
const assetHost = isProd ? 'https://github.githubassets.com/' : '/'
const gridSteps = {x: 0, y: 0}
let safeGridArea = {
  xStart: 0,
  xEnd: 0,
  yStart: 0,
  yEnd: 0,
}
const overlayingElements = [
  {query: 'header.HeaderMktg, #header', horizmod: 0, vertmod: 8},
  {query: '.js-startups-hero-fg-detect', horizmod: 24, vertmod: 0},
]
let excludedGridAreas = [
  {
    xStart: 0,
    xEnd: 0,
    yStart: 0,
    yEnd: 0,
  },
]
const idealGridStepSize = 200 * ppi
let gridStepSize = 0
const lookXMax = 4
const lookYMax = 4
const buildInStagger = 40
let needsUpdate = false
let startOfAnimation = Date.now()
interface fgElement {
  type: string
  name: string
  x: number
  y: number
  image?: HTMLImageElement
  scale: number
  delay: number
  loaded: boolean
}
let fgElements: fgElement[] = []
let fgGrid: unknown[][] = []

const COLORS = {
  fgDefault: '#fff',
  avatarStroke: 'rgba(116, 131, 255, 0.4)',
  iconStroke: 'rgba(116, 131, 255, 0.4)',
  iconFill: '#fff',
  vectorPointFill: '#d6fd2a',
  vectorLineStroke: 'rgba(95, 104, 179, 0.4)',
  subtle: '#272f3b',
  muted: '#1c222e',
}

const USERS = approved_handles['handles'] || []
const AVATAR_IMAGE_EXTENSION = '.jpg'

const ICONS = ['check.svg', 'folder.svg', 'heart.svg', 'person.svg', 'shield.svg', 'star.svg', 'thumbs.svg', 'fire.svg']

observe('.js-startups-hero-bg', canvas => {
  if (!(canvas instanceof HTMLCanvasElement)) return
  const ctx = canvas.getContext('2d')
  if (!ctx) return

  setResolution(canvas)
  calculateGridFromCanvas(canvas)
  drawGrid(canvas, ctx)

  // eslint-disable-next-line github/prefer-observers
  window.addEventListener('resize', () => {
    setResolution(canvas)
    calculateGridFromCanvas(canvas)
    drawGrid(canvas, ctx)
  })
})

observe('.js-startups-hero-fg', canvas => {
  if (!(canvas instanceof HTMLCanvasElement)) return
  const ctx = canvas.getContext('2d')
  if (!ctx) return

  setResolution(canvas)
  calculateGridFromCanvas(canvas)
  drawForeground(canvas, ctx)

  // eslint-disable-next-line github/prefer-observers
  window.addEventListener('resize', () => {
    setResolution(canvas)
    calculateGridFromCanvas(canvas)
    drawForeground(canvas, ctx)
  })
})

function drawForeground(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D) {
  if (gridSteps.x === 0) return
  needsUpdate = false
  const elapsed = Date.now() - startOfAnimation
  ctx.clearRect(0, 0, canvas.width, canvas.height)

  // Draw lines between all elements
  for (let y = safeGridArea.yStart; y <= safeGridArea.yEnd; y++) {
    for (let x = safeGridArea.xStart; x <= safeGridArea.xEnd; x++) {
      const gridPoint = fgGrid[y]![x]
      if (!gridPoint) continue

      // Always draw to connect a line to the end of the canvas, if it's at the left or right edge
      if (x === safeGridArea.xStart) {
        drawLineBetweenPoints(0, y, x, y, ctx)
      } else if (x === safeGridArea.xEnd) {
        drawLineBetweenPoints(x, y, x + 1, y, ctx)
      }

      // Check above, vertically, for the first hit
      for (let lookY = 1; lookY <= lookYMax; lookY++) {
        const hitY = y - lookY
        // Don't outside safe area on Y axis
        if (hitY < safeGridArea.yStart) continue

        const hit = fgGrid[hitY]![x]
        if (hit) {
          drawLineBetweenPoints(x, y, x, hitY, ctx)
          break
        }
      }

      // Check to the right, horizontally
      for (let lookX = 1; lookX <= lookXMax; lookX++) {
        // Skip if looking at the same spot
        const hitX = x + lookX
        // Don't look outside of safe area on X axis
        if (hitX > safeGridArea.xEnd) continue

        const hit = fgGrid[y]![hitX]
        if (hit) {
          drawLineBetweenPoints(x, y, hitX, y, ctx)
          break
        }
      }

      // Check to the top right, and top left
      const hitTopRight = fgGrid[y - 1]![x + 1]
      if (hitTopRight) {
        drawCurveBetweenPoints(x, y, x + 1, y - 1, ctx)
      }
      const hitTopLeft = fgGrid[y - 1]![x - 1]
      if (hitTopLeft) {
        drawCurveBetweenPoints(x, y, x - 1, y - 1, ctx)
      }
      // TODO: Only draw curved lines that align with the grid (even/uneven)
    }
  }

  // Draw all elements
  for (let i = 0; i < fgElements.length; i++) {
    const element: fgElement = fgElements[i]!
    const type = element.type
    const readyToAnimate = type === 'vectorPoint' || element.loaded
    // Set new scale
    if (element.scale < 1) needsUpdate = true
    if (element.delay < elapsed && readyToAnimate) {
      const sizeDiff = 1 - element.scale
      element.scale = 1 - sizeDiff * 0.8
      if (element.scale > 0.98) element.scale = 1
    }

    // Transform from position in grid to pixels
    const point = {
      x: (gridStepSize / 2) * element.x,
      y: (gridStepSize / 2) * element.y,
    }

    if (type === 'avatar' && element.loaded && element.image !== undefined) {
      drawAvatarAtPoint(element.image, point.x, point.y, element.scale, ctx)
    } else if (type === 'icon' && element.loaded && element.image !== undefined) {
      drawIconAtPoint(element.image, point.x, point.y, element.scale, ctx)
    } else if (type === 'vectorPoint') {
      drawVectorPointAtPoint(point.x, point.y, element.scale, ctx)
    }
  }

  if (needsUpdate) window.requestAnimationFrame(() => drawForeground(canvas, ctx))
}

function drawCurveBetweenPoints(x1: number, y1: number, x2: number, y2: number, ctx: CanvasRenderingContext2D) {
  // Find circle center
  const y = x1 > x2 && y1 > y2 ? y1 : y2
  const x = Math.min(x1, x2)
  const point = {
    x: (gridStepSize / 2) * x,
    y: (gridStepSize / 2) * y,
  }
  const offset = x1 > x2 && y1 > y2 ? -Math.PI / 2 : 0
  // Draw here
  ctx.lineWidth = 3 * ppi
  // Draw the lines
  ctx.strokeStyle = COLORS.vectorLineStroke
  ctx.beginPath()
  ctx.arc(point.x, point.y, gridStepSize / 2, offset, Math.PI / 2 + offset)
  ctx.stroke()
}

function drawLineBetweenPoints(x1: number, y1: number, x2: number, y2: number, ctx: CanvasRenderingContext2D) {
  ctx.lineWidth = 3 * ppi
  // Draw the lines
  ctx.strokeStyle = COLORS.vectorLineStroke
  ctx.beginPath()
  const point1 = {
    x: (gridStepSize / 2) * x1,
    y: (gridStepSize / 2) * y1,
  }
  const point2 = {
    x: (gridStepSize / 2) * x2,
    y: (gridStepSize / 2) * y2,
  }
  ctx.moveTo(point1.x, point1.y)
  ctx.lineTo(point2.x, point2.y)
  ctx.stroke()
}

function generateForegroundElements() {
  fgGrid = []
  for (let y = 0; y < gridSteps.y * 2; y++) {
    fgGrid.push(Array(gridSteps.x * 2 + 1))
  }

  fgElements = []
  const randomElements = []

  for (let i = 0; i < avatarCount; i++) {
    const userImg = document.createElement('img')
    const randomUser = USERS[randomIntFromInterval(0, USERS.length - 1)]!
    const userElement: fgElement = {
      type: 'avatar',
      name: randomUser,
      image: userImg,
      scale: 0,
      loaded: false,
      x: 0,
      y: 0,
      delay: 0,
    }
    randomElements.push(userElement)
    // Load image
    userImg.src = `${assetHost}images/modules/site/avatars/${randomUser}${AVATAR_IMAGE_EXTENSION}?width=${avatarSize}`
    userImg.onload = function () {
      userElement.loaded = true
    }
  }

  for (let i = 0; i < ICONS.length; i++) {
    const iconImg = document.createElement('img')
    const icon: fgElement = {
      type: 'icon',
      name: ICONS[i]!,
      image: iconImg,
      scale: 0,
      loaded: false,
      x: 0,
      y: 0,
      delay: 0,
    }
    iconImg.src = `${assetHost}images/modules/site/enterprise/startups/icons/${icon.name}`
    randomElements.push(icon)
    // Load icon
    iconImg.onload = function () {
      icon.loaded = true
    }
  }

  for (let i = 0; i < vectorPointCount; i++) {
    const vectorPoint: fgElement = {
      type: 'vectorPoint',
      name: 'vector',
      scale: 0.2,
      loaded: true,
      x: 0,
      y: 0,
      delay: 0,
    }
    randomElements.push(vectorPoint)
  }

  // Shuffle elements
  for (let i = randomElements.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    const temp: fgElement = randomElements[i]!
    randomElements[i] = randomElements[j]
    randomElements[j] = temp
  }

  let elementIndex = 0
  for (let y = safeGridArea.yStart; y <= safeGridArea.yEnd; y++) {
    for (let x = safeGridArea.xStart; x <= safeGridArea.xEnd; x++) {
      if (isWithinExcludedArea(x, y)) continue
      if (elementIndex >= randomElements.length) continue
      // Only draw elements a certain percentage of the time
      if (randomIntFromInterval(1, 6) > 1) continue

      const element: fgElement = randomElements[elementIndex]!
      element.x = x
      element.y = y
      element.delay = buildInStagger * elementIndex
      fgElements.push(element)
      // Figure out what's going on!
      fgGrid[y]![x] = element
      elementIndex++
    }
  }

  // Enable animations to run
  needsUpdate = true
  startOfAnimation = Date.now()
}

function isWithinExcludedArea(x: number, y: number) {
  x = (gridStepSize / 2) * x
  y = (gridStepSize / 2) * y

  for (const excludedGridArea of excludedGridAreas) {
    if (
      x >= excludedGridArea.xStart &&
      x <= excludedGridArea.xEnd &&
      y >= excludedGridArea.yStart &&
      y <= excludedGridArea.yEnd
    )
      return true
  }
  return false
}

function drawAvatarAtPoint(
  image: HTMLImageElement,
  x: number,
  y: number,
  scale: number,
  ctx: CanvasRenderingContext2D,
) {
  const size = avatarSize * scale
  ctx.save()
  ctx.beginPath()
  ctx.arc(x, y, size / 2, 0, Math.PI * 2, true)

  ctx.lineWidth = avatarStrokeWidth
  ctx.strokeStyle = COLORS.avatarStroke
  ctx.stroke()

  ctx.closePath()
  ctx.clip()

  ctx.drawImage(image, x - size / 2, y - size / 2, size, size)

  ctx.beginPath()
  ctx.arc(x, y, size / 2, 0, Math.PI * 2, true)
  ctx.clip()
  ctx.closePath()
  ctx.restore()
}

function drawVectorPointAtPoint(x: number, y: number, scale: number, ctx: CanvasRenderingContext2D, color?: string) {
  const size = (vectorPointSize * scale) / 2
  ctx.beginPath()
  ctx.arc(x, y, size, 0, Math.PI * 2, true)
  ctx.fillStyle = color || COLORS.vectorPointFill
  ctx.fill()
}

function drawIconAtPoint(image: HTMLImageElement, x: number, y: number, scale: number, ctx: CanvasRenderingContext2D) {
  ctx.beginPath()
  const size = iconSize * scale
  ctx.arc(x, y, size / 2, 0, Math.PI * 2, true)
  ctx.lineWidth = iconStrokeWidth
  ctx.strokeStyle = COLORS.iconStroke
  ctx.stroke()
  ctx.fillStyle = COLORS.iconFill
  ctx.fill()
  ctx.drawImage(image, x - size / 2 + iconInset, y - size / 2 + iconInset, size - iconInset * 2, size - iconInset * 2)
}

function randomIntFromInterval(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1) + min)
}

function drawGrid(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D) {
  if (gridSteps.x === 0) return
  ctx.clearRect(0, 0, canvas.width, canvas.height)

  ctx.lineWidth = 1 * ppi
  // Draw the lines
  ctx.strokeStyle = COLORS.muted
  for (let y = 0; y < gridSteps.y * 2; y++) {
    ctx.beginPath()
    const yPos = (gridStepSize / 2) * y + gridStepSize / 2
    ctx.moveTo(0, yPos)
    ctx.lineTo(canvas.width, yPos)
    ctx.stroke()
  }
  for (let x = 0; x < gridSteps.x * 2; x++) {
    ctx.beginPath()
    const xPos = (gridStepSize / 2) * x + gridStepSize / 2
    ctx.moveTo(xPos, 0)
    ctx.lineTo(xPos, canvas.height)
    ctx.stroke()
  }

  // Draw the dots
  ctx.fillStyle = COLORS.muted
  for (let y = 0; y < gridSteps.y * 2; y++) {
    for (let x = 0; x < gridSteps.x * 2; x++) {
      ctx.beginPath()
      ctx.strokeStyle = COLORS.muted
      ctx.arc((gridStepSize / 2) * x, (y * gridStepSize) / 2, 5, 0, 2 * Math.PI)
      ctx.fill()
    }
  }

  // Draw the subtle grid
  ctx.strokeStyle = COLORS.muted
  for (let y = 0; y < gridSteps.y + 1; y++) {
    for (let x = 0; x < gridSteps.x + 1; x++) {
      ctx.beginPath()
      ctx.arc(gridStepSize * x, y * gridStepSize, gridStepSize / 2, 0, 2 * Math.PI)
      ctx.stroke()
    }
  }

  // Draw the main grid
  ctx.strokeStyle = COLORS.subtle
  for (let y = 0; y < gridSteps.y; y++) {
    for (let x = 0; x < gridSteps.x; x++) {
      ctx.beginPath()
      ctx.arc(
        gridStepSize * x + gridStepSize / 2,
        gridStepSize / 2 + y * gridStepSize,
        gridStepSize / 2,
        0,
        2 * Math.PI,
      )
      ctx.stroke()
    }
  }
}

function setResolution(canvas: HTMLCanvasElement) {
  const clientRect = canvas.getBoundingClientRect()
  canvas.width = Math.max(window.innerWidth, clientRect.width) * ppi
  canvas.height = Math.max(window.innerHeight, clientRect.height) * ppi
}

// Similar to getBoundingClientRect but relative to the parent element
function getElementRect(element: HTMLElement, horizmod = 0, vertmod = 0) {
  let xStart = element.offsetLeft - horizmod
  let yStart = element.offsetTop - vertmod

  xStart = xStart > 0 ? xStart : 0
  yStart = yStart > 0 ? yStart : 0

  let xEnd = xStart + element.offsetWidth + horizmod * 2
  let yEnd = yStart + element.offsetHeight + vertmod * 2

  xStart *= ppi
  yStart *= ppi
  xEnd *= ppi
  yEnd *= ppi

  return {
    xStart,
    yStart,
    xEnd,
    yEnd,
  }
}

// Builds a set of rects that are excluded from the foreground drawing
function buildExcludedAreasSet(
  canvas: HTMLCanvasElement,
  overlayingElementsQuerySet: Array<{
    elements?: NodeListOf<HTMLElement>
    query: string
    horizmod: number
    vertmod: number
  }>,
) {
  const excludedAreasSet = []

  for (const queryItem of overlayingElementsQuerySet) {
    // Use cached elements if available
    const elements: NodeListOf<HTMLElement> | null =
      queryItem.hasOwnProperty('elements') && queryItem.elements?.length
        ? queryItem.elements
        : document.querySelectorAll(queryItem.query)

    if (!elements) continue
    // Cache the query results
    if (!queryItem.hasOwnProperty('elements') && !queryItem.elements?.length) queryItem.elements = elements

    for (const el of elements) {
      const excludedArea = getElementRect(el, queryItem.horizmod, queryItem.vertmod)

      //TODO: Could be a better check if the excluded area is inside the canvas. But it's not needed for now.
      if (excludedArea.xStart < canvas.width && excludedArea.yStart < canvas.height) excludedAreasSet.push(excludedArea)
    }
  }

  return excludedAreasSet
}

function calculateGridFromCanvas(canvas: HTMLCanvasElement) {
  // If gridSteps x has changed, relayout composition
  // Even if the composition doesn't need to be regenerated, the sizing parameters has to
  const gridStepsX = Math.round(canvas.width / idealGridStepSize)

  gridStepSize = canvas.width / gridStepsX
  const gridStepsY = Math.round(canvas.height / gridStepSize + 1)
  const needsReLayout = gridStepsX !== gridSteps.x || gridSteps.y !== gridStepsY
  gridSteps.y = gridStepsY
  gridSteps.x = gridStepsX

  // Exit early if we don't need to do any more work
  if (!needsReLayout) return

  // Where foreground elements can be drawn (set with gridSteps)
  safeGridArea = {
    xStart: 1,
    yStart: 1,
    xEnd: gridSteps.x * 2 - 1,
    yEnd: gridSteps.y + 3,
  }

  // A rect set where we don't draw things behind the header and the CTA
  excludedGridAreas = buildExcludedAreasSet(canvas, overlayingElements)

  generateForegroundElements()
}

observe('#billingCountry', billingCountryInput => {
  billingCountryInput.addEventListener('change', event => {
    const targetElement: HTMLInputElement = event.target as HTMLInputElement
    const requiredTaxIdCountries = [
      'AE',
      'AM',
      'AZ',
      'BY',
      'BR',
      'CN',
      'HU',
      'IN',
      'IQ',
      'KZ',
      'KG',
      'MD',
      'MM',
      'PL',
      'RU',
      'SA',
      'SS',
      'TJ',
      'TH',
      'TR',
      'UA',
      'UZ',
      'VE',
      'VN',
      'ZA',
    ]
    const taxID = document.getElementById('taxID')
    const taxIDInput: HTMLInputElement | null = document.getElementById('billing_taxID') as HTMLInputElement
    if (!taxID || !taxIDInput || !billingCountryInput) return

    const selectedCountry = targetElement.value?.trim()

    if (selectedCountry !== '' && requiredTaxIdCountries.includes(selectedCountry)) {
      taxID.hidden = false
      taxIDInput.classList.add('required')
      taxIDInput.required = true
    } else {
      taxID.hidden = true
      taxIDInput.classList.remove('required')
      taxIDInput.required = false
    }
  })
})
